import React from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/utils/cn';
import BookActionsMenu from './BookActionsMenu';
import { toPersianNumber } from '@/utils/persianNumber';

interface BookCardProps {
  book: {
    book_id: number;
    title: string;
    author: string;
    cover_image_url?: string | null;
    price?: string;
    topic?: string;
    description?: string;
    average_rating?: number;
    rating?: number;
  };
  className?: string;
  progress?: number;
  showProgressBelow?: boolean;
  isSaved?: boolean;
  showActions?: boolean;
  onClick?: () => void;
  titleClassName?: string;
  coverClassName?: string;
  showAuthor?: boolean;
  authorClassName?: string;
}

const BookCard: React.FC<BookCardProps> = ({ book, className, progress, showProgressBelow, isSaved, showActions, onClick, titleClassName, coverClassName, showAuthor, authorClassName }) => {
  const navigate = useNavigate();

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else {
      navigate(`/library/book`, { state: { bookId: book.book_id } });
    }
  };

  return (
    <div 
      className={cn(
        'group relative w-full cursor-pointer',
        className
      )}
      onClick={handleClick}
    >
      <div className={cn('relative aspect-[2/3] rounded-xl overflow-hidden shadow-[0_2px_7px_rgba(15,23,42,0.3)]', coverClassName)}>
        {/* Cover Image */}
        {book.cover_image_url ? (
          <img
            src={book.cover_image_url}
            alt={book.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500">
            <div className="text-6xl mb-2">📚</div>
          </div>
        )}
        
        {showActions && (
          <div className="absolute top-2 left-2 z-[4]" onClick={(e) => e.stopPropagation()}>
            <BookActionsMenu 
              bookId={book.book_id} 
              title={book.title} 
              author={book.author} 
              isSaved={isSaved}
              triggerClassName="px-1 py-1 rounded-md bg-black/60 hover:bg-black/70 active:scale-95 transition flex items-center justify-center gap-1 text-white"
              iconClassName="text-white w-3 h-3"
            />
          </div>
        )}

      </div>

      {/* Progress bar outside cover - only for reading books */}
      {progress != null && !Number.isNaN(Number(progress)) && Number(progress) >= 0 && showProgressBelow && (
        <div className="mt-2 w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-blue-400 to-indigo-500 transition-all duration-300"
            style={{ width: `${Math.max(0, Math.min(100, Number(progress)))}%` }}
          />
        </div>
      )}

      <div className="mt-2 flex flex-col gap-2">
        <h3 className={cn('font-bold text-foreground text-sm leading-snug font-persian line-clamp-2 text-center', titleClassName)}>
          {toPersianNumber(book.title)}
        </h3>
        {showAuthor && (
          <p className={cn('text-xs text-muted-foreground font-persian line-clamp-1', authorClassName)}>
            {toPersianNumber(book.author)}
          </p>
        )}
      </div>
    </div>
  );
};

export default React.memo(BookCard);
